import { NetworkStatus, useQuery } from '@apollo/client';
import { Col, Empty, Input, Modal, Row, Spin } from 'antd';
import { get, isFunction, isObject } from 'lodash';
import { MagnifyingGlass } from 'phosphor-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import InView from './InView';

const LIMIT = 12;

const useDebounce = (value, timeout = 500) => {
  const [state, setState] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => {
      setState(value);
    }, timeout);

    return () => clearTimeout(id);
  }, [value, timeout]);

  return state;
};

const ModalContent = ({
  query,
  dataSelector,
  variablesSelector,
  renderItem,
  keyField = 'id',
  limit = LIMIT,
  keys,
  search,
  filters,
  queryOptions = {}
}) => {
  const ref = useRef();

  const debouncedSearch = useDebounce(search);

  const { fetchMore, data, networkStatus } = useQuery(query, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: variablesSelector(0, limit, debouncedSearch, filters),
    ...queryOptions
  });

  const records = useMemo(() => (data ? dataSelector(data) : []), [data]);
  const count = useMemo(
    () => (data && isObject(keys) ? get(data, [keys.data, keys.count], 0) : 0),
    [data]
  );

  const hasMoreData = count > records.length;

  const loading =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables;
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  const fetchMoreRecords = useCallback(
    ({ offset, search: searchValue, filters: filtersValue } = {}) => {
      fetchMore({
        variables: variablesSelector(offset, limit, searchValue, filtersValue),
        updateQuery: (prev, { fetchMoreResult }) => {
          if (
            fetchMoreResult &&
            keys &&
            isObject(keys) &&
            'data' in keys &&
            'records' in keys &&
            'count' in keys
          ) {
            return {
              ...prev,
              [keys.data]: {
                ...get(prev, keys.data),
                [keys.records]: [
                  ...get(prev, [keys.data, keys.records]),
                  ...get(fetchMoreResult, [keys.data, keys.records])
                ],
                [keys.count]: get(fetchMoreResult, [keys.data, keys.count])
              }
            };
          }
          return { ...prev };
        }
      });
    },
    [fetchMore]
  );

  return (
    <div className="selectable-modal-body" ref={ref}>
      {loading ? (
        <div className="selectable-modal-wrapper">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {!records.length ? (
            <div className="selectable-modal-wrapper">
              <Empty />
            </div>
          ) : (
            <>
              <Row gutter={[16, 16]}>
                {records.map((record) => (
                  <Col
                    xs={6}
                    key={
                      isFunction(keyField) ? keyField(record) : record[keyField]
                    }
                  >
                    {renderItem(record)}
                  </Col>
                ))}
              </Row>
              {isFetchingMore && (
                <div className="d-flex justify-center pt-16">
                  <Spin />
                </div>
              )}
              {!isFetchingMore && hasMoreData && !loading && (
                <InView
                  ref={ref}
                  onChange={({ inView }) => {
                    if (inView) {
                      fetchMoreRecords({
                        offset: records.length,
                        search,
                        filters
                      });
                    }
                  }}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

const SelectableModal = ({
  open,
  onClose,
  title = 'Select Record',
  filters,
  renderFilters,
  ...rest
}) => {
  const [search, setSearch] = useState('');

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onClose}
      width="90%"
      className="selectable-modal"
      footer={null}
      destroyOnClose
    >
      <Row className="mb-12">
        <Col xs={8}>
          <Input
            placeholder="Search..."
            allowClear
            className="search-component"
            name="search"
            value={search}
            onChange={handleSearchChange}
            prefix={<MagnifyingGlass size={20} />}
          />
        </Col>
        <Col xs={16} className="d-flex justify-end align-center">
          {renderFilters &&
            isFunction(renderFilters) &&
            renderFilters({ search })}
        </Col>
      </Row>
      {open && <ModalContent filters={filters} search={search} {...rest} />}
    </Modal>
  );
};

export default SelectableModal;
